import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';

import { useGetInfoApi } from '@/api/notice';
import defaultImg from '@/assets/img/defaultImg.jpg';
import InputSearch from '@/components/common/Input/InputSearch/InputSearch';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import TablePagination from '@/components/common/Pagination/TablePagination';
import PostList from '@/components/common/PostList/PostList.tsx';
import BoardTable from '@/components/common/Table/BoardTable/BoardTable';
import { normalizeInformationMenu } from '@/constants/information';
import { tableOptions } from '@/constants/option';
import { generateColumns } from '@/constants/table';
import useDebounce from '@/hooks/useDebounce';
import useIsMobile from '@/hooks/useIsMobile';
import { convertInfoValue } from '@/utils/convertValue';

import * as S from './style';

const InformationNotice = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isMobile = useIsMobile(900);
	const location = useLocation();

	const params = normalizeInformationMenu(location.state?.menu);
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [selectedOption, setSelectedOption] = useState(t('information.raw.searchCategory.all'));
	const debouncedSearch = useDebounce(search, 300);

	const { data: infoData } = useGetInfoApi({
		page: page,
		search: debouncedSearch,
		search_category: selectedOption,
		type: convertInfoValue(params),
	});

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	};
	const handleGetPk = (pk: number | string) => {
		navigate(`/information/notice/${pk}`, { state: { menu: params } });
	};

	const getDynamicField = (g: any, field: string) => {
		const type = convertInfoValue(params);
		return g[`${type}_${field}`] || '';
	};

	return (
		<S.ConferenceContainer>
			<S.ConferenceBoardBox>
				<S.ConferenceSearchBox>
					<InputSelect options={tableOptions} selectedOption={selectedOption} setSelectedOption={setSelectedOption} />
					<InputSearch value={search} input={handleSearch} placeholder="information.search.placeholder" />
				</S.ConferenceSearchBox>
				{infoData && infoData[convertInfoValue(params)]?.length !== 0 ? (
					convertInfoValue(params) !== 'gallery' ? (
						!isMobile ? (
							<TablePagination activePage={page} totalItems={infoData?.[`${convertInfoValue(params)}_count`]} onChange={(page) => setPage(page)}>
								<BoardTable
									columns={generateColumns(infoData[convertInfoValue(params)] ?? [])}
									page={page}
									data={infoData[convertInfoValue(params)] ?? []}
									totalItems={infoData?.[`${convertInfoValue(params)}_count`]}
									onClick={handleGetPk}
								/>
							</TablePagination>
						) : (
							<TablePagination activePage={page} totalItems={infoData?.[`${convertInfoValue(params)}_count`]} onChange={(page) => setPage(page)}>
								<PostList data={infoData[convertInfoValue(params)] ?? []} sort={convertInfoValue(params)} />
							</TablePagination>
						)
					) : (
						<TablePagination activePage={page} totalItems={infoData?.gallery_count} onChange={(page) => setPage(page)}>
							<S.ConferenceGalleryBox>
								{infoData &&
									(infoData[convertInfoValue(params)] ?? []).map((g, index) => (
										<S.GalleryItemBox key={index} onClick={() => navigate(`/information/notice/${getDynamicField(g, 'pk')}`, { state: { menu: params } })}>
											<div className="img_box">
												<img src={getDynamicField(g, 'image_url') || defaultImg} alt={t('information.list.galleryAlt')} />
											</div>
											<S.ContentBox>
												<p className="title">{getDynamicField(g, 'title')}</p>
												<p className="date">{dayjs(getDynamicField(g, 'enrollment_date')).format('YYYY-MM-DD')}</p>
											</S.ContentBox>
										</S.GalleryItemBox>
									))}
							</S.ConferenceGalleryBox>
						</TablePagination>
					)
				) : (
					<S.NoData>{t('information.list.noData')}</S.NoData>
				)}
			</S.ConferenceBoardBox>
		</S.ConferenceContainer>
	);
};

export default InformationNotice;
